import {
  faBuilding,
  faCaretDown,
  faCaretRight,
  faFolder,
  faGlobe,
  faLock,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ObjectRepository, RefBuilder } from '@framework/repository';
import { RTDBPath } from 'balus-common';
import { Loading } from '@framework/ui/atoms';
import { classNames } from '@framework/utils';
import { Workspace } from '@workspace/domain/workspace';
import { Folder } from '@workspace/view-model-folder/domain/Folder';
import { RootFolderTree } from '@workspace/view-model-folder/domain/FolderTree';
import type { FolderId, ViewModelId, WorkspaceId, WorkspaceInternalPublicMemberRoleJSON } from 'balus-common';
import { useCallback, useEffect, useMemo, useState } from 'react';

type FetchResult =
  | {
      workspace: Workspace;
      internalPublicSetting: WorkspaceInternalPublicMemberRoleJSON;
      rootFolderTree: RootFolderTree;
      allFolders: Record<FolderId, Folder>;
      currentFolderId: FolderId | null;
      selectedFolderId: FolderId | null;
    }
  | 'not-found';

const fetchFolderTrees = async (workspaceId: WorkspaceId, itemId: FolderId | ViewModelId): Promise<FetchResult> => {
  const workspace = await new ObjectRepository(Workspace, RTDBPath.Workspace.workspaceEntityPath(workspaceId)).get();
  if (!workspace) return 'not-found';

  const internalPublicSetting = (
    await RefBuilder.ref(RTDBPath.Group.internalPublicWorkspacePath(workspace.groupId, workspace.id)).get()
  ).val();

  const rootFolderTree = await new ObjectRepository(
    RootFolderTree,
    RTDBPath.Workspace.rootFolderTreePath(workspace.id)
  ).get();
  if (!rootFolderTree) return 'not-found';

  const folders = await Promise.all(
    rootFolderTree
      .getAllFolderIds()
      .map(async (folderId) => new ObjectRepository(Folder, RTDBPath.Workspace.folderPath(folderId)).get())
  );

  const parentFolderId = rootFolderTree.getParentFolderId(itemId) || null;

  return {
    workspace,
    internalPublicSetting,
    rootFolderTree,
    allFolders: Object.fromEntries(folders.filter((folder) => !!folder).map((folder) => [folder.id, folder])),
    currentFolderId: parentFolderId,
    selectedFolderId: parentFolderId,
  };
};

type Props = {
  workspaceId: WorkspaceId;
  itemId: ViewModelId;
  onChangeSelectedFolder(folder: Folder | null): void;
};

export const FolderSelector: React.FC<Props> = ({ workspaceId, itemId, onChangeSelectedFolder }: Props) => {
  const [state, setState] = useState<FetchResult | null>(null);
  const [openFolders, setOpenFolders] = useState<Record<FolderId, boolean>>({});

  useEffect(() => {
    fetchFolderTrees(workspaceId, itemId).then((result) => {
      setState(result);

      // 現在のフォルダとその祖先フォルダを展開状態にする
      if (result !== null && result !== 'not-found' && result.currentFolderId) {
        const ancestorFolderIds = result.rootFolderTree.getAncestorFolderIds(result.currentFolderId);
        setOpenFolders(
          Object.fromEntries([...ancestorFolderIds, result.currentFolderId].map((folderId) => [folderId, true]))
        );
      }
    });
  }, [itemId, workspaceId]);

  const handleChangeSelectedFolderId = useCallback(
    (folderId: FolderId) => {
      if (state === null || state === 'not-found') {
        onChangeSelectedFolder(null);
        return;
      }

      const selectedFolder = state.allFolders[folderId] || Folder.buildRootFolder(workspaceId);
      onChangeSelectedFolder(state.currentFolderId === selectedFolder.id ? null : selectedFolder);
      setState({ ...state, selectedFolderId: selectedFolder.id });
    },
    [onChangeSelectedFolder, state, workspaceId]
  );

  const toggleFolder = useCallback((folderId: FolderId) => {
    setOpenFolders((prev) => ({
      ...prev,
      [folderId]: !prev[folderId],
    }));
  }, []);

  return (
    <>
      {state === null ? (
        <Loading />
      ) : state === 'not-found' ? (
        <div>フォルダ情報の取得に失敗しました。</div>
      ) : (
        <div className="mb-4 mt-2 overflow-y-auto" style={{ minHeight: '250px', maxHeight: '50vh' }}>
          <WorkspaceItemComponent
            workspace={state.workspace}
            internalPublicSetting={state.internalPublicSetting}
            selectedFolderId={state.selectedFolderId}
            setSelectedFolderId={handleChangeSelectedFolderId}
          />

          <FolderListComponent
            folderId={Folder.ROOT_ID}
            rootFolderTree={state.rootFolderTree}
            allFolders={state.allFolders}
            excludeFolderId={itemId}
            openFolders={openFolders}
            toggleFolder={toggleFolder}
            selectedFolderId={state.selectedFolderId}
            setSelectedFolderId={handleChangeSelectedFolderId}
          />
        </div>
      )}
    </>
  );
};

type WorkspaceItemProps = {
  workspace: Workspace;
  internalPublicSetting: WorkspaceInternalPublicMemberRoleJSON;
  selectedFolderId: FolderId | null;
  setSelectedFolderId(folderId: FolderId): void;
};

const WorkspaceItemComponent: React.FC<WorkspaceItemProps> = ({
  workspace,
  internalPublicSetting,
  selectedFolderId,
  setSelectedFolderId,
}: WorkspaceItemProps) => {
  return (
    <div className="my-1 flex items-center">
      <span className="fa-layers fa-fw mr-1 shrink-0 text-brand">
        <FontAwesomeIcon icon={faFolder} />
        <FontAwesomeIcon
          icon={
            workspace.isPersonal
              ? faUser
              : workspace.isPublicSpace
                ? faGlobe
                : internalPublicSetting
                  ? faBuilding
                  : faLock
          }
          inverse
          transform="shrink-8 down-1"
        />
      </span>
      <span
        className={classNames(
          'grow cursor-pointer hover:bg-gray-400',
          selectedFolderId === Folder.ROOT_ID ? 'bg-gray-300' : ''
        )}
        onClick={() => setSelectedFolderId(Folder.ROOT_ID)}
      >
        {workspace.name.toString()}
      </span>
    </div>
  );
};

type FolderListProps = {
  folderId: FolderId;
  rootFolderTree: RootFolderTree;
  allFolders: Record<FolderId, Folder>;
  excludeFolderId: FolderId;
  openFolders: Record<FolderId, boolean>;
  toggleFolder(folderId: FolderId): void;
  selectedFolderId: FolderId | null;
  setSelectedFolderId(folderId: FolderId): void;
};

const FolderListComponent: React.FC<FolderListProps> = ({
  folderId,
  rootFolderTree,
  allFolders,
  excludeFolderId,
  openFolders,
  toggleFolder,
  selectedFolderId,
  setSelectedFolderId,
}: FolderListProps) => {
  const currentFolders = useMemo(() => {
    return rootFolderTree
      .getFolderIdsOf(folderId)
      .map((folderId) => allFolders[folderId])
      .filter((folder) => !!folder)
      .filter((folder) => folder.id !== excludeFolderId)
      .sort(Folder.compare);
  }, [allFolders, excludeFolderId, folderId, rootFolderTree]);

  return (
    <div>
      {currentFolders.map((folder) => {
        const isOpen = openFolders[folder.id];

        return (
          <div key={folder.id} className="my-1">
            <div className="flex items-center">
              <button className="mr-1 focus:outline-none" onClick={() => toggleFolder(folder.id)}>
                <FontAwesomeIcon icon={isOpen ? faCaretDown : faCaretRight} className="size-4" />
              </button>

              <span
                className={classNames(
                  'grow cursor-pointer hover:bg-gray-400',
                  selectedFolderId === folder.id ? 'bg-gray-300' : ''
                )}
                onClick={() => {
                  setSelectedFolderId(folder.id);
                  if (!isOpen) toggleFolder(folder.id);
                }}
              >
                <FontAwesomeIcon icon={faFolder} className="mr-1 text-brand" />
                {folder.name}
              </span>
            </div>

            {isOpen && (
              <div className="ml-4">
                <FolderListComponent
                  folderId={folder.id}
                  rootFolderTree={rootFolderTree}
                  allFolders={allFolders}
                  excludeFolderId={excludeFolderId}
                  openFolders={openFolders}
                  toggleFolder={toggleFolder}
                  selectedFolderId={selectedFolderId}
                  setSelectedFolderId={setSelectedFolderId}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
